import { IconChevronDown, IconWorld } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  formatReportingClock,
  REPORTING_TIME_ZONES,
  type ReportingTimeZone,
} from "@/lib/reporting-time-zone";

interface ReportingTimeZoneSelectProps {
  value: ReportingTimeZone;
  onValueChange: (value: ReportingTimeZone) => void;
}

export function ReportingTimeZoneSelect({
  value,
  onValueChange,
}: ReportingTimeZoneSelectProps): React.ReactElement {
  const { t } = useTranslation("layout");
  const label = t(
    value === "America/Los_Angeles" ? "reportingTimeZone.pacific" : "reportingTimeZone.beijing",
  );

  function selectTimeZone(nextValue: unknown): void {
    if (REPORTING_TIME_ZONES.some((timeZone) => timeZone === nextValue)) {
      onValueChange(nextValue as ReportingTimeZone);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="outline"
            size="sm"
            aria-label={`${t("reportingTimeZone.title")}: ${label}`}
            className="w-[140px] justify-between"
          />
        }
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <IconWorld className="size-4" aria-hidden />
          <span className="truncate">{label}</span>
        </span>
        <IconChevronDown data-icon="inline-end" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="min-w-[220px]">
        <DropdownMenuRadioGroup value={value} onValueChange={selectTimeZone}>
          <DropdownMenuLabel>{t("reportingTimeZone.title")}</DropdownMenuLabel>
          <DropdownMenuGroup>
            <DropdownMenuRadioItem value="America/Los_Angeles">
              <span className="flex flex-col">
                <span>{t("reportingTimeZone.pacific")}</span>
                <span className="text-xs text-muted-foreground">
                  {t("reportingTimeZone.pacificHint")}
                </span>
              </span>
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="Asia/Shanghai">
              <span className="flex flex-col">
                <span>{t("reportingTimeZone.beijing")}</span>
                <span className="text-xs text-muted-foreground">
                  {t("reportingTimeZone.beijingHint")}
                </span>
              </span>
            </DropdownMenuRadioItem>
          </DropdownMenuGroup>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ReportingClock({ value }: { value: ReportingTimeZone }): React.ReactElement {
  const [now, setNow] = useState(() => new Date());
  const clock = formatReportingClock(now, value);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(interval);
  }, []);

  return (
    <time
      dateTime={now.toISOString()}
      title={clock.abbreviation}
      className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground"
    >
      {clock.dateTime}
    </time>
  );
}
